import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import NavigationHeader from '../../components/ui/NavigationHeader';
import AdminSidebar from '../../components/ui/AdminSidebar';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import StatusIndicatorPanel from '../../components/ui/StatusIndicatorPanel';
import ThemeToggle from '../../components/ui/ThemeToggle';
import MetricsOverview from './components/MetricsOverview';
import PerformanceCharts from './components/PerformanceCharts';
import RecentActivity from './components/RecentActivity';
import RealTimeMonitor from './components/RealTimeMonitor';
import FinancialDashboard from './components/FinancialDashboard';
import AlertsPanel from './components/AlertsPanel';
import WalletOverviewPanel from './components/WalletOverviewPanel';
import Icon from '../../components/AppIcon';
import WhatsAppKPIPanel from './components/WhatsAppKPIPanel';
import { adminStatsService, transactionService } from '../../services/supabaseService';
import useInactivityTimeout from '../../hooks/useInactivityTimeout';
import ReAuthModal from '../../components/ReAuthModal';
import { logAdminAction, AUDIT_ACTIONS } from '../../services/auditLogService';
import AuditLogPanel from './components/AuditLogPanel';
import DriverReviewQueue from './components/DriverReviewQueue';
import VehicleReviewQueue from './components/VehicleReviewQueue';
import AdminVehicleUpload from './components/AdminVehicleUpload';
import CorporateLeaseRequestsQueue from './components/CorporateLeaseRequestsQueue';
import CorporateAccountsPanel from './components/CorporateAccountsPanel';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [refreshInterval, setRefreshInterval] = useState(30000);
  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Re-auth modal state
  const [reAuthOpen, setReAuthOpen] = useState(false);
  const [reAuthAction, setReAuthAction] = useState(null); // { label, callback }

  // Refs to child refresh functions
  const financialRefreshRef = useRef(null);
  const alertsRefreshRef = useRef(null);

  // Read active panel from URL query param
  const getActivePanel = () => {
    const params = new URLSearchParams(location?.search);
    return params?.get('panel') || 'overview';
  };
  const activePanel = getActivePanel();

  // Auth guard
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        navigate('/admin-login', { replace: true });
        return;
      }
      setAuthUser(session?.user);
      setAuthLoading(false);
    };
    checkAuth();

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        navigate('/admin-login', { replace: true });
      } else {
        setAuthUser(session?.user);
      }
    });

    return () => subscription?.unsubscribe();
  }, [navigate]);

  // ── Inactivity timeout ──────────────────────────────────────────────────────
  const handleInactivityTimeout = useCallback(async () => {
    await supabase?.auth?.signOut();
    navigate('/admin-login', { replace: true, state: { reason: 'inactivity' } });
  }, [navigate]);

  const { timeoutWarning, remainingSeconds, dismissWarning } = useInactivityTimeout({
    onTimeout: handleInactivityTimeout,
    enabled: !authLoading && !!authUser,
  });
  // ────────────────────────────────────────────────────────────────────────────

  const user = {
    name: authUser?.email?.split('@')?.[0] || 'Admin User',
    email: authUser?.email || 'admin@onthego.co.ke',
    role: 'System Administrator',
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logAdminAction({
        actionType: AUDIT_ACTIONS?.LOGOUT,
        actorName: user?.name,
        notes: `Admin signed out: ${user?.email}`,
      });
      await supabase?.auth?.signOut();
      navigate('/admin-login', { replace: true });
    } catch (err) {
      console.error('Sign out error:', err);
    } finally {
      setSigningOut(false);
    }
  };

  // ── Re-auth helpers ─────────────────────────────────────────────────────────
  const requireReAuth = (label, callback) => {
    setReAuthAction({ label, callback });
    setReAuthOpen(true);
  };

  const handleReAuthSuccess = () => {
    setReAuthOpen(false);
    reAuthAction?.callback?.();
    setReAuthAction(null);
  };

  const handleReAuthCancel = () => {
    setReAuthOpen(false);
    setReAuthAction(null);
  };
  // ────────────────────────────────────────────────────────────────────────────

  const timeRanges = [
    { value: '1h', label: 'Last Hour' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
  ];

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
    }, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    setRefreshTrigger(prev => prev + 1);
    if (financialRefreshRef?.current) await financialRefreshRef?.current();
    if (alertsRefreshRef?.current) await alertsRefreshRef?.current();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  // Export — requires re-auth
  const doExport = async () => {
    setExportLoading(true);
    try {
      const [stats, transactions] = await Promise.all([
        adminStatsService?.getDashboardStats(),
        transactionService?.getAllTransactions({ limit: 200 }),
      ]);
      const csvRows = [
        ['OnTheGo Admin Dashboard Export'],
        [`Generated: ${new Date()?.toLocaleString()}`],
        [`Time Range: ${selectedTimeRange}`],
        [],
        ['=== DASHBOARD STATS ==='],
        ['Metric', 'Value'],
        ['Total Orders', stats?.totalOrders],
        ['Active Orders', stats?.activeOrders],
        ['Online Drivers', stats?.onlineDrivers],
        ['Total Drivers', stats?.totalDrivers],
        ['Total Customers', stats?.totalCustomers],
        ['Total Revenue (KES)', stats?.totalRevenue?.toFixed(2)],
        [],
        ['=== RECENT TRANSACTIONS ==='],
        ['ID', 'User', 'Type', 'Status', 'Amount (KES)', 'Payment Method', 'Date'],
        ...transactions?.map(t => [
          t?.id?.slice(0, 8),
          t?.userName || 'Unknown',
          t?.type,
          t?.status,
          parseFloat(t?.amount || 0)?.toFixed(2),
          t?.paymentMethod || 'mpesa',
          t?.createdAt ? new Date(t.createdAt)?.toLocaleString() : '',
        ]),
      ];
      const csvContent = csvRows?.map(row => row?.join(','))?.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `onthego-dashboard-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);

      // Log the export action
      await logAdminAction({
        actionType: AUDIT_ACTIONS?.EXPORT_DASHBOARD,
        actorName: user?.name,
        notes: `Dashboard data exported (time range: ${selectedTimeRange})`,
        metadata: { timeRange: selectedTimeRange, rowCount: transactions?.length },
      });
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleExport = () => {
    requireReAuth('export dashboard data', doExport);
  };

  const handleTimeRangeChange = (range) => {
    setSelectedTimeRange(range);
  };

  const handleRefreshIntervalChange = (interval) => {
    setRefreshInterval(interval);
  };

  // Panel label for breadcrumb
  const getPanelLabel = () => {
    const labels = {
      'overview': 'Overview', 'system-health': 'System Health', 'monitor': 'Real-time Monitor',
      'customers': 'Customer Accounts', 'drivers': 'Driver Management', 'admins': 'Administrator Roles',
      'registrations': 'Registration Requests', 'rides': 'Ride Coordination', 'deliveries': 'Delivery Management',
      'dispatch': 'Dispatcher Tools', 'fleet': 'Fleet Management', 'revenue': 'Revenue Tracking',
      'mpesa': 'M-Pesa Integration', 'commissions': 'Commission Management', 'settlements': 'Settlement Processing',
      'analytics': 'Business Intelligence', 'reports': 'Performance Reports', 'market': 'Market Analysis',
      'custom-reports': 'Custom Reports', 'config': 'Platform Configuration', 'security': 'Security Settings',
      'api': 'API Management', 'logs': 'System Logs', 'support': 'Customer Service',
      'notifications': 'Notification Center', 'communications': 'Communication Tools', 'feedback': 'Feedback Management',
    };
    return labels?.[activePanel] || 'Overview';
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        {/* Skeleton Header */}
        <div className="fixed top-0 left-0 right-0 z-40 h-16 bg-surface border-b border-border flex items-center px-4 lg:px-6 gap-4">
          <div className="w-8 h-8 rounded-lg bg-border animate-pulse" />
          <div className="w-32 h-5 rounded-md bg-border animate-pulse" />
          <div className="flex-1" />
          <div className="w-24 h-8 rounded-lg bg-border animate-pulse" />
          <div className="w-8 h-8 rounded-full bg-border animate-pulse" />
        </div>
        {/* Skeleton Sidebar */}
        <div className="hidden lg:flex fixed left-0 top-16 bottom-0 w-72 bg-surface border-r border-border flex-col p-4 gap-3">
          {[...Array(8)]?.map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2">
              <div className="w-5 h-5 rounded bg-border animate-pulse" />
              <div className="flex-1 h-4 rounded bg-border animate-pulse" style={{ animationDelay: `${i * 60}ms` }} />
            </div>
          ))}
        </div>
        {/* Skeleton Main Content */}
        <main className="lg:ml-72 pt-16">
          <div className="p-4 lg:p-6 space-y-6">
            {/* Page Title Skeleton */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="space-y-2">
                <div className="w-52 h-8 rounded-lg bg-border animate-pulse" />
                <div className="w-72 h-4 rounded bg-border animate-pulse" />
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32 h-9 rounded-lg bg-border animate-pulse" />
                <div className="w-9 h-9 rounded-lg bg-border animate-pulse" />
                <div className="w-24 h-9 rounded-lg bg-border animate-pulse" />
              </div>
            </div>
            {/* Status Banner Skeleton */}
            <div className="h-14 rounded-lg bg-border animate-pulse" />
            {/* Metrics Cards Skeleton */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)]?.map((_, i) => (
                <div key={i} className="bg-surface rounded-xl border border-border p-5 space-y-3" style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-xl bg-border animate-pulse" />
                    <div className="w-14 h-5 rounded-full bg-border animate-pulse" />
                  </div>
                  <div className="w-24 h-7 rounded-md bg-border animate-pulse" />
                  <div className="w-32 h-4 rounded bg-border animate-pulse" />
                </div>
              ))}
            </div>
            {/* Charts Skeleton */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {[...Array(2)]?.map((_, i) => (
                <div key={i} className="bg-surface rounded-xl border border-border p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-36 h-5 rounded bg-border animate-pulse" />
                    <div className="w-20 h-7 rounded-lg bg-border animate-pulse" />
                  </div>
                  <div className="h-48 rounded-lg bg-border animate-pulse" />
                </div>
              ))}
            </div>
            {/* Bottom Grid Skeleton */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 space-y-6">
                {/* Recent Activity Skeleton */}
                <div className="bg-surface rounded-xl border border-border p-5 space-y-4">
                  <div className="w-36 h-5 rounded bg-border animate-pulse" />
                  {[...Array(5)]?.map((_, i) => (
                    <div key={i} className="flex items-center gap-3" style={{ animationDelay: `${i * 60}ms` }}>
                      <div className="w-9 h-9 rounded-full bg-border animate-pulse flex-shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <div className="w-3/4 h-4 rounded bg-border animate-pulse" />
                        <div className="w-1/2 h-3 rounded bg-border animate-pulse" />
                      </div>
                      <div className="w-16 h-6 rounded-full bg-border animate-pulse" />
                    </div>
                  ))}
                </div>
                {/* Alerts Skeleton */}
                <div className="bg-surface rounded-xl border border-border p-5 space-y-3">
                  <div className="w-28 h-5 rounded bg-border animate-pulse" />
                  {[...Array(3)]?.map((_, i) => (
                    <div key={i} className="h-14 rounded-lg bg-border animate-pulse" style={{ animationDelay: `${i * 80}ms` }} />
                  ))}
                </div>
              </div>
              <div className="space-y-6">
                {/* Wallet Panel Skeleton */}
                <div className="bg-surface rounded-xl border border-border p-5 space-y-3">
                  <div className="w-32 h-5 rounded bg-border animate-pulse" />
                  <div className="h-24 rounded-xl bg-border animate-pulse" />
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-16 rounded-lg bg-border animate-pulse" />
                    <div className="h-16 rounded-lg bg-border animate-pulse" />
                  </div>
                </div>
                {/* Real-time Monitor Skeleton */}
                <div className="bg-surface rounded-xl border border-border p-5 space-y-3">
                  <div className="w-36 h-5 rounded bg-border animate-pulse" />
                  {[...Array(4)]?.map((_, i) => (
                    <div key={i} className="flex items-center justify-between" style={{ animationDelay: `${i * 60}ms` }}>
                      <div className="w-24 h-4 rounded bg-border animate-pulse" />
                      <div className="w-16 h-6 rounded-full bg-border animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Render panel content based on active panel
  const renderPanelContent = () => {
    // Audit log panel
    if (activePanel === 'audit-log') {
      return <AuditLogPanel />;
    }

    // Vehicle review panel
    if (activePanel === 'vehicle-review') {
      return (
        <>
          <VehicleReviewQueue />
          <AdminVehicleUpload />
        </>
      );
    }

    // Corporate lease requests panel
    if (activePanel === 'corporate-leases') {
      return <CorporateLeaseRequestsQueue />;
    }

    // Corporate accounts panel
    if (activePanel === 'corporate-accounts') {
      return <CorporateAccountsPanel />;
    }

    // Panels that show the main dashboard overview
    if (activePanel === 'overview' || activePanel === 'monitor' || activePanel === 'system-health') {
      return (
        <>
          {/* System Status Banner */}
          <div className="bg-surface rounded-lg border border-border p-4">
            <StatusIndicatorPanel position="banner" />
          </div>
          <MetricsOverview timeRange={selectedTimeRange} refreshTrigger={refreshTrigger} />
          <PerformanceCharts timeRange={selectedTimeRange} refreshTrigger={refreshTrigger} />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-6">
              <RecentActivity />
              <AlertsPanel onRefresh={(fn) => { alertsRefreshRef.current = fn; }} />
            </div>
            <div className="space-y-6">
              <WalletOverviewPanel />
              <RealTimeMonitor />
              <FinancialDashboard onRefresh={(fn) => { financialRefreshRef.current = fn; }} />
            </div>
          </div>
          <WhatsAppKPIPanel />
        </>
      );
    }

    // WhatsApp channel panel
    if (activePanel === 'whatsapp') {
      return (
        <>
          <WhatsAppKPIPanel />
        </>
      );
    }

    // Financial panels
    if (['revenue', 'mpesa', 'commissions', 'settlements']?.includes(activePanel)) {
      return (
        <>
          <MetricsOverview timeRange={selectedTimeRange} refreshTrigger={refreshTrigger} />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <WalletOverviewPanel />
            </div>
            <div>
              <FinancialDashboard onRefresh={(fn) => { financialRefreshRef.current = fn; }} />
            </div>
          </div>
        </>
      );
    }

    // Analytics panels
    if (['analytics', 'reports', 'market', 'custom-reports']?.includes(activePanel)) {
      return (
        <>
          <MetricsOverview timeRange={selectedTimeRange} refreshTrigger={refreshTrigger} />
          <PerformanceCharts timeRange={selectedTimeRange} refreshTrigger={refreshTrigger} />
        </>
      );
    }

    // Operations panels
    if (['rides', 'deliveries', 'dispatch', 'fleet']?.includes(activePanel)) {
      return (
        <>
          <MetricsOverview timeRange={selectedTimeRange} refreshTrigger={refreshTrigger} />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <RecentActivity />
            </div>
            <div>
              <RealTimeMonitor />
            </div>
          </div>
        </>
      );
    }

    // User management panels
    if (['customers', 'drivers', 'admins', 'registrations']?.includes(activePanel)) {
      return (
        <>
          <MetricsOverview timeRange={selectedTimeRange} refreshTrigger={refreshTrigger} />
          <DriverReviewQueue />
          <RecentActivity />
        </>
      );
    }

    // Support panels
    if (['support', 'notifications', 'communications', 'feedback']?.includes(activePanel)) {
      return (
        <>
          <AlertsPanel onRefresh={(fn) => { alertsRefreshRef.current = fn; }} />
          <RecentActivity />
        </>
      );
    }

    // System panels
    if (['config', 'security', 'api', 'logs']?.includes(activePanel)) {
      return (
        <>
          <div className="bg-surface rounded-lg border border-border p-4">
            <StatusIndicatorPanel position="banner" />
          </div>
          <AlertsPanel onRefresh={(fn) => { alertsRefreshRef.current = fn; }} />
        </>
      );
    }

    // Default fallback — show full overview
    return (
      <>
        <div className="bg-surface rounded-lg border border-border p-4">
          <StatusIndicatorPanel position="banner" />
        </div>
        <MetricsOverview timeRange={selectedTimeRange} refreshTrigger={refreshTrigger} />
        <PerformanceCharts timeRange={selectedTimeRange} refreshTrigger={refreshTrigger} />
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            <RecentActivity />
            <AlertsPanel onRefresh={(fn) => { alertsRefreshRef.current = fn; }} />
          </div>
          <div className="space-y-6">
            <WalletOverviewPanel />
            <RealTimeMonitor />
            <FinancialDashboard onRefresh={(fn) => { financialRefreshRef.current = fn; }} />
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* ── Inactivity Warning Banner ─────────────────────────────────────── */}
      {timeoutWarning && (
        <div className="fixed top-0 left-0 right-0 z-[150] bg-warning text-white px-4 py-3 flex items-center justify-between gap-4 shadow-lg">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Icon name="Clock" size={16} />
            <span>
              Session expiring due to inactivity — you will be logged out in{' '}
              <strong>{remainingSeconds}s</strong>
            </span>
          </div>
          <button
            onClick={dismissWarning}
            className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-colors flex-shrink-0"
          >
            <Icon name="RefreshCw" size={14} />
            Stay Logged In
          </button>
        </div>
      )}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <NavigationHeader
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        onSignOut={handleSignOut}
        signingOut={signingOut}
      />
      <AdminSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <main className={`lg:ml-72 pt-16 ${timeoutWarning ? 'mt-12' : ''}`}>
        {/* Maintenance Banner */}
        {maintenanceMode && (
          <div className="bg-warning text-white px-4 py-3 flex items-center justify-center space-x-3 text-sm font-medium">
            <Icon name="AlertTriangle" size={18} />
            <span>⚠️ MAINTENANCE MODE ACTIVE — Platform is under maintenance. New bookings are suspended.</span>
            <Icon name="AlertTriangle" size={18} />
          </div>
        )}
        <div className="p-4 lg:p-6 space-y-6">
          {/* Dashboard Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                Admin Dashboard
              </h1>
              <p className="text-text-secondary mt-1">
                {getPanelLabel()} — Comprehensive operational oversight
              </p>
            </div>

            {/* Dashboard Controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              {/* Time Range Selector */}
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} color="var(--color-text-secondary)" />
                <select
                  value={selectedTimeRange}
                  onChange={(e) => handleTimeRangeChange(e?.target?.value)}
                  className="input-field text-sm py-1 px-2 min-w-0"
                >
                  {timeRanges?.map((range) => (
                    <option key={range?.value} value={range?.value}>
                      {range?.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Refresh Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                  className="p-2 rounded-lg border border-border hover:bg-surface-hover transition-colors duration-200 disabled:opacity-50"
                  title="Refresh data"
                >
                  <Icon name="RefreshCw" size={16} className={isRefreshing ? 'animate-spin' : ''} />
                </button>

                <select
                  value={refreshInterval}
                  onChange={(e) => handleRefreshIntervalChange(Number(e?.target?.value))}
                  className="input-field text-sm py-1 px-2 min-w-0"
                  title="Auto-refresh interval"
                >
                  <option value={10000}>10s</option>
                  <option value={30000}>30s</option>
                  <option value={60000}>1m</option>
                  <option value={300000}>5m</option>
                </select>
              </div>

              {/* Theme Toggle */}
              <ThemeToggle variant="pill" />

              {/* Export Button — requires re-auth */}
              <button
                onClick={handleExport}
                disabled={exportLoading}
                className="btn-secondary text-sm flex items-center space-x-2 disabled:opacity-50"
              >
                <Icon name="Download" size={16} />
                <span>{exportLoading ? 'Exporting...' : 'Export'}</span>
              </button>

              {/* Sign Out Button */}
              <button
                onClick={handleSignOut}
                disabled={signingOut}
                className="btn-secondary text-sm flex items-center space-x-2 text-error border-error hover:bg-error-50 disabled:opacity-50"
              >
                <Icon name="LogOut" size={16} />
                <span>{signingOut ? 'Signing out...' : 'Sign Out'}</span>
              </button>
            </div>
          </div>

          {/* Panel Content */}
          {renderPanelContent()}
        </div>
      </main>
      <QuickActionToolbar
        onMaintenanceChange={(val) => {
          requireReAuth(
            val ? 'enable maintenance mode' : 'disable maintenance mode',
            async () => {
              setMaintenanceMode(val);
              await logAdminAction({
                actionType: val ? AUDIT_ACTIONS?.MAINTENANCE_MODE_ON : AUDIT_ACTIONS?.MAINTENANCE_MODE_OFF,
                actorName: user?.name,
                notes: `Maintenance mode ${val ? 'enabled' : 'disabled'} by ${user?.email}`,
              });
            }
          );
        }}
      />
      {/* Re-Authentication Modal */}
      <ReAuthModal
        isOpen={reAuthOpen}
        actionLabel={reAuthAction?.label || 'perform this action'}
        onSuccess={handleReAuthSuccess}
        onCancel={handleReAuthCancel}
      />
    </div>
  );
};

export default AdminDashboard;